import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { RelationComponentBox } from "./RelationComponentBox";
import { RelationArrow } from "./RelationArrow";

type Relation = {
  id: string;
  source_id: string;
  target_id: string;
  source_name: string;
  target_name: string;
  created_at: string;
  source_component_type?: {
    icon?: string | null;
  };
  source_organization_name?: string;
  target_component_type?: {
    icon?: string | null;
  };
  target_organization_name?: string;
  source_component?: {
    component_types?: {
      icon?: string | null;
    };
    organizations?: {
      name?: string;
    };
  };
  target_component?: {
    component_types?: {
      icon?: string | null;
    };
    organizations?: {
      name?: string;
    };
  };
};

type RelationsListProps = {
  relations: Relation[] | undefined;
  isLoading: boolean;
};

export const RelationsList = ({ relations, isLoading }: RelationsListProps) => {
  const [sortColumn, setSortColumn] = useState<string>("source_name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedRelations = relations ? [...relations].sort((a, b) => {
    const valueA = a[sortColumn as keyof typeof a];
    const valueB = b[sortColumn as keyof typeof b];

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortDirection === "asc" 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA);
    }
    return 0;
  }) : [];

  return (
    <Card className="border border-border/40 shadow-sm">
      <CardContent className="p-6">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Relacionamentos do Componente</h3>
        <Table>
          <TableHeader>
            <TableRow className="bg-primary/5 hover:bg-primary/10">
              <TableHead 
                className="text-primary font-medium cursor-pointer"
                onClick={() => handleSort("source_name")}
              >
                Componente Origem {sortColumn === "source_name" && (sortDirection === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead 
                className="text-primary font-medium cursor-pointer"
                onClick={() => handleSort("target_name")}
              >
                Componente Destino {sortColumn === "target_name" && (sortDirection === "asc" ? "↑" : "↓")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8">
                  <div className="flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">Carregando relacionamentos...</p>
                </TableCell>
              </TableRow>
            ) : sortedRelations?.length ? (
              sortedRelations.map((relation) => (
                <TableRow key={relation.id} className="hover:bg-muted/50">
                  <TableCell colSpan={2} className="p-4">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex-1">
                        <RelationComponentBox
                          name={relation.source_name}
                          organizationName={
                            relation.source_organization_name || 
                            relation.source_component?.organizations?.name
                          }
                          componentType={
                            relation.source_component_type || 
                            relation.source_component?.component_types
                          }
                        />
                      </div>
                      <RelationArrow />
                      <div className="flex-1">
                        <RelationComponentBox
                          name={relation.target_name}
                          organizationName={
                            relation.target_organization_name || 
                            relation.target_component?.organizations?.name
                          }
                          componentType={
                            relation.target_component_type || 
                            relation.target_component?.component_types
                          }
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8">
                  <p className="text-sm text-muted-foreground">Nenhum relacionamento encontrado.</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};